import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import axios from "axios";

import API, { showNetworkErrorToast } from "Utils/api";

import { RowData } from "Components/NewList/types";
import {
  reorderMissionsQueue,
  setIsMissionPlannerUpdating,
  setMissionCounts,
  setMissionsQueue,
} from ".";
import { UNASSIGNED_MISSIONS_KEY } from "Slices/missionsPage";

let controller: AbortController | null = null;
// DEV-3710: the live tab-badge refresh (countersOnly) fires autonomously off WS
// frames and can overlap a user-driven full fetch (tab click / date / search).
// It gets its OWN abort controller so a pending counts refresh can never abort —
// and thereby drop the row update of — an in-flight full fetch. The two request
// kinds only cancel a prior request of their own kind.
let countersController: AbortController | null = null;

export const fetchGetMissionQueue = createAsyncThunk<
  void,
  {
    currentFleetId: string;
    period: MissionPlannerPeriodFilter;
    limit: number;
    searchValue: string;
    activeTab: MissionPlannerTabName;
    selectedVehicleIds: Array<string>;
    countersOnly?: boolean;
  }
>(
  "missionQueue",
  async (
    data: {
      currentFleetId: string;
      period: MissionPlannerPeriodFilter;
      limit: number;
      searchValue: string;
      activeTab: MissionPlannerTabName;
      selectedVehicleIds: Array<string>;
      countersOnly?: boolean;
    },
    thunkAPI
  ) => {
    // Abort only a prior in-flight request of the SAME kind; a full fetch and a
    // counters-only refresh use independent controllers so they don't clobber
    // each other (see countersController declaration above).
    if (data.countersOnly) {
      if (countersController) {
        countersController.abort();
        countersController = null;
      }
    } else if (controller) {
      controller.abort();
      controller = null;
    }
    const queryStatus =
      data.activeTab === "All" ? "" : `&status=${data.activeTab.toLowerCase()}`;

    const querySearch = `&name=${data.searchValue}`;
    const startDate = `&created_start=${data.period.startDate}`;
    const endDate = `&created_end=${data.period.endDate}`;
    // A counters-only refresh ignores `results`; fetch a single row so a live
    // tab-badge update doesn't pull the full paginated list. The count /
    // deployedCount / completedCount aggregates are independent of limit.
    const limit = data.countersOnly ? "&limit=1" : `&limit=${data.limit}`;
    if (!data.countersOnly) {
      thunkAPI.dispatch(setIsMissionPlannerUpdating(true));
    }
    try {
      // DEV-3710: do not send vehicle_id__in to the backend — that filter
      // drops missions with vehicle=null (fleet-unassigned) from the response.
      // Instead, receive all fleet missions and filter client-side so that
      // unassigned missions are always visible regardless of the vehicle picker.
      const queryParameters = `?fleet_ids=${data.currentFleetId}${queryStatus}${startDate}${endDate}${querySearch}${limit}`;
      const activeController = new AbortController();
      if (data.countersOnly) {
        countersController = activeController;
      } else {
        controller = activeController;
      }
      const response = await API.get(
        `fleets/mission_queue/${queryParameters}`,
        {
          signal: activeController.signal,
        }
      );
      // Clear only OUR slot, and only if a newer same-kind request hasn't
      // already replaced it — otherwise an older request resolving late would
      // null a newer in-flight controller (harmless to the newer request, but
      // it would lose its abortability).
      if (data.countersOnly) {
        if (countersController === activeController) countersController = null;
      } else if (controller === activeController) {
        controller = null;
      }
      thunkAPI.dispatch(setMissionCounts(response.data));
      if (!data.countersOnly) {
        const allResults: Mission[] = response.data.results ?? [];
        // When a vehicle filter is active, show missions that belong to a
        // selected vehicle OR have no vehicle (fleet-unassigned). With no
        // filter, show everything.
        const filtered =
          data.selectedVehicleIds.length > 0
            ? allResults.filter(
                (m) =>
                  m.vehicle == null ||
                  data.selectedVehicleIds.includes(m.vehicle as string)
              )
            : allResults;
        thunkAPI.dispatch(setMissionsQueue(filtered));
      }
      await thunkAPI.dispatch(setIsMissionPlannerUpdating(false));
    } catch (error) {
      await thunkAPI.dispatch(setIsMissionPlannerUpdating(false));
      if (axios.isCancel(error)) return;
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const cancelMission = createAsyncThunk<
  void,
  { missionId: string; vehicleId: string | null | undefined }
>(
  "missionQueue",
  async (
    data: { missionId: string; vehicleId: string | null | undefined },
    thunkAPI
  ) => {
    try {
      // Uber missions (Call Vehicle) have no assigned vehicle until the
      // assignment engine picks one — vehicleId is null/undefined. The
      // vehicle-scoped cancel URL would interpolate the literal string
      // "null" and 404. Fall back to the fleet-scoped cancel in that case.
      // Also guard against UNASSIGNED_MISSIONS_KEY to keep both cancel
      // paths in lockstep and prevent drift if Queue Manager ever routes
      // through this thunk in the future.
      const hasVehicle =
        data.vehicleId &&
        data.vehicleId !== "null" &&
        data.vehicleId !== "undefined" &&
        data.vehicleId !== UNASSIGNED_MISSIONS_KEY;
      const url = hasVehicle
        ? `vehicles/${data.vehicleId}/mission_queue/${data.missionId}/cancel/`
        : `fleets/mission_queue/${data.missionId}/cancel/`;
      await API.post(url);
      // WS updates for CANCELED status are not always delivered; refresh
      // the queue so the UI reflects the actual state from the backend.
      const { missionPlanner } = thunkAPI.getState() as {
        missionPlanner: {
          currentFleetId: string | null;
          period: MissionPlannerPeriodFilter | null;
          limit: number;
          searchValue: string;
          activeTab: MissionPlannerTabName;
          selectedVehicleIds: string[];
        };
      };
      if (missionPlanner.currentFleetId && missionPlanner.period) {
        thunkAPI.dispatch(
          fetchGetMissionQueue({
            currentFleetId: missionPlanner.currentFleetId,
            period: missionPlanner.period,
            limit: missionPlanner.limit,
            searchValue: missionPlanner.searchValue,
            activeTab: missionPlanner.activeTab,
            selectedVehicleIds: missionPlanner.selectedVehicleIds,
          })
        );
      }
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const changeMissionOrder = createAsyncThunk<
  void,
  {
    missionId: string;
    order: number;
    cancelAction: () => RowData[];
    newMissionsOrder: RowData[];
  }
>(
  "missionQueue",
  async (
    data: {
      missionId: string;
      order: number;
      cancelAction: () => RowData[];
      newMissionsOrder: RowData[];
    },
    thunkAPI
  ) => {
    thunkAPI.dispatch(
      reorderMissionsQueue(data.newMissionsOrder.map((item) => item.id))
    );
    try {
      await API.patch(`fleets/mission_queue/${data.missionId}/`, {
        order: data.order,
      });
      toast.success("Mission reordered successfully");
    } catch (error) {
      const oldItems = data.cancelAction();
      thunkAPI.dispatch(reorderMissionsQueue(oldItems.map((item) => item.id)));

      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);
